/**
 * EventCard — reusable renderer for a single firm event.
 *
 * Used on:
 *   - /events (browse hub)
 *   - /how-to-get-into/{firm} (firm page "Upcoming from {firm}" strip)
 *   - /degree/... (small event card matching subject_area)
 *   - /firm-portal/{slug} (Events tab, with owner-visible moderation state)
 *
 * variants:
 *   "public" (default) — dark card, RSVP CTA, no admin/status detail
 *   "owner"            — adds status chip + cancel button
 *   "compact"          — one-line summary for degree-page inline surfaces
 */
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Video, ExternalLink, Trash2, ShieldCheck, Pencil } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const EVENT_TYPE_LABEL = {
  insight_week: "Insight Week",
  internship: "Internship",
  spring_week: "Spring Week",
  open_day: "Open Day",
  networking: "Networking",
  webinar: "Webinar",
  conference: "Conference",
};

const STATUS_STYLE = {
  pending_review: "border-amber-300 bg-amber-50 text-amber-900",
  approved: "border-emerald-300 bg-emerald-50 text-emerald-900",
  rejected: "border-red-300 bg-red-50 text-red-900",
  cancelled: "border-stone-300 bg-stone-100 text-stone-500",
};

// Weekday-aware formatter. Same-day events render as
//   "Wed 27 Oct · 12:10 – 15:11"
// Multi-day events keep both dates in view but drop the times on
// end (matches how UK "insight weeks" are conventionally read):
//   "Wed 27 Oct 12:10 → Sat 30 Oct 17:00"
// All output is en-GB and hits the reader's local timezone.
function fmtDateTimeRange(startIso, endIso) {
  if (!startIso) return "";
  try {
    const s = new Date(startIso);
    const e = endIso ? new Date(endIso) : null;
    const dOpts = { weekday: "short", day: "numeric", month: "short" };
    const tOpts = { hour: "2-digit", minute: "2-digit", hour12: false };
    const sDate = s.toLocaleDateString("en-GB", dOpts);
    const sTime = s.toLocaleTimeString("en-GB", tOpts);
    if (!e) return `${sDate} · ${sTime}`;
    const sameDay = s.toDateString() === e.toDateString();
    const eDate = e.toLocaleDateString("en-GB", dOpts);
    const eTime = e.toLocaleTimeString("en-GB", tOpts);
    if (sameDay) return `${sDate} · ${sTime} – ${eTime}`;
    return `${sDate} ${sTime} → ${eDate} ${eTime}`;
  } catch {
    return startIso;
  }
}

// Two-line place descriptor. Priority:
//   1. uni-hosted → { primary: uni_name, secondary: city }
//   2. off-campus w/ venue → { primary: location, secondary: `${city} · Firm venue` }
//   3. legacy (only `location` stored) → { primary: location, secondary: "" }
//   4. nothing → { primary: "Location TBC", secondary: "" }
// This is what makes the "make time and place clearer" pass show
// the campus name as the headline instead of the bare city.
function placeLines(event) {
  const uni = (event.university_name || "").trim();
  const city = (event.city || "").trim();
  const loc = (event.location || "").trim();
  const outsideSentinel = (event.university_id || "").trim() === "outside";
  if (uni) return { primary: uni, secondary: city || loc };
  if (outsideSentinel) {
    return { primary: loc || "Firm venue", secondary: city ? `${city} · Firm venue` : "Firm venue" };
  }
  if (loc || city) return { primary: loc || city, secondary: (loc && city && loc !== city) ? city : "" };
  return { primary: "Location TBC", secondary: "" };
}

// Google Maps iframe URL — free `output=embed` style, no API key
// needed. Same trick used by /universities/{id}/map-embed. We URL-
// encode the address string and centre the pin on it. Returns null
// for virtual events or when there is nothing to geocode.
function mapEmbedUrl(event) {
  if (event.is_virtual) return null;
  const uni = (event.university_name || "").trim();
  const loc = (event.location || "").trim();
  const city = (event.city || "").trim();
  const q = [loc, uni, city].filter(Boolean).join(", ");
  if (!q) return null;
  return `https://www.google.com/maps?q=${encodeURIComponent(q)}&z=15&output=embed`;
}

export default function EventCard({ event, variant = "public", onCancel, onEdit, showFirm = true, showMap = true }) {
  if (variant === "compact") return <CompactEventCard event={event} />;

  const typeLabel = EVENT_TYPE_LABEL[event.event_type] || event.event_type;
  const isOwner = variant === "owner";
  const statusStyle = STATUS_STYLE[event.status] || STATUS_STYLE.pending_review;
  const place = placeLines(event);
  const mapUrl = showMap ? mapEmbedUrl(event) : null;

  return (
    <article
      data-testid={`event-card-${event.id}`}
      data-event-id={event.id}
      className="rounded-xl border border-stone-200 bg-white p-5 hover:border-[#4C1D95]/30 transition-colors"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-underground-sc tracking-[0.18em] text-[#4C1D95] bg-[#F5F0FF] border border-[#4C1D95]/20 rounded-full px-2 py-0.5">
            {typeLabel}
          </span>
          {isOwner && event.status && (
            <span
              data-testid={`event-status-${event.id}`}
              className={`text-[10px] font-underground-sc tracking-wider rounded-full border px-2 py-0.5 ${statusStyle}`}
            >
              {event.status.replace("_", " ")}
            </span>
          )}
          {event.is_expired && (
            <span className="text-[10px] font-underground-sc tracking-wider rounded-full border border-stone-300 bg-stone-50 text-stone-500 px-2 py-0.5">
              Expired
            </span>
          )}
        </div>
        {showFirm && event.firm_slug && (
          <Link
            to={`/how-to-get-into/${event.firm_slug}`}
            data-testid={`event-firm-link-${event.id}`}
            className="text-xs text-stone-500 hover:text-[#4C1D95] font-medium"
          >
            {event.firm_name || event.firm_slug} →
          </Link>
        )}
      </header>

      <h3 className="mt-3 text-lg font-editorial text-stone-900 leading-tight">
        {event.title}
      </h3>

      <p className="mt-2 text-sm text-stone-700 leading-relaxed line-clamp-3">
        {event.description}
      </p>

      {/* Time & place — headline sizes on the primary lines so
          "Wed 27 Oct · 12:10 – 15:11" and "Imperial College London"
          are the two things a skimming student actually sees. */}
      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-stone-700">
        <div className="flex items-start gap-2">
          <CalendarDays className="h-4 w-4 mt-0.5 text-[#4C1D95] shrink-0" />
          <div className="min-w-0">
            <div
              data-testid={`event-time-${event.id}`}
              className="text-sm font-semibold text-stone-900 leading-tight"
            >
              {fmtDateTimeRange(event.starts_at, event.ends_at)}
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">Local time</div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          {event.is_virtual ? (
            <Video className="h-4 w-4 mt-0.5 text-[#4C1D95] shrink-0" />
          ) : (
            <MapPin className="h-4 w-4 mt-0.5 text-[#4C1D95] shrink-0" />
          )}
          <div className="min-w-0" data-testid={`event-place-${event.id}`}>
            <div className="text-sm font-semibold text-stone-900 leading-tight truncate">
              {event.is_virtual ? "Virtual" : place.primary}
            </div>
            {!event.is_virtual && place.secondary && (
              <div className="text-[11px] text-stone-500 mt-0.5 truncate">
                {place.secondary}
              </div>
            )}
          </div>
        </div>
      </dl>

      {mapUrl && (
        // 180-tall map on the card so the RSVP button + description
        // remain the focal point; sandbox="" strips any script/form
        // capabilities we don't want cross-origin — a static pin is
        // all we need. `loading="lazy"` keeps the /events browse hub
        // snappy when 30+ cards mount at once.
        <div
          data-testid={`event-map-${event.id}`}
          className="mt-3 rounded-md overflow-hidden border border-stone-200"
        >
          <iframe
            title={`Map — ${event.title}`}
            src={mapUrl}
            width="100%"
            height="180"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            style={{ border: 0 }}
          />
        </div>
      )}

      {(event.target_year_groups?.length > 0
        || event.target_subjects?.length > 0
        || event.target_sectors?.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(event.target_year_groups || []).map((yg) => (
            <span key={yg} className="text-[10px] font-underground-sc tracking-wider rounded-full border border-stone-200 bg-stone-50 text-stone-600 px-2 py-0.5">
              {yg}
            </span>
          ))}
          {(event.target_subjects || []).map((s) => (
            <span key={s} className="text-[10px] font-underground-sc tracking-wider rounded-full border border-stone-200 bg-stone-50 text-stone-600 px-2 py-0.5">
              {s}
            </span>
          ))}
          {(event.target_sectors || []).map((s) => (
            <span key={`sec-${s}`} className="text-[10px] font-underground-sc tracking-wider rounded-full border border-[#4C1D95]/20 bg-[#F5F0FF] text-[#4C1D95] px-2 py-0.5">
              {s}
            </span>
          ))}
        </div>
      )}

      <footer className="mt-4 flex flex-wrap items-center justify-between gap-2">
        {event.rsvp_url ? (
          <a
            href={event.rsvp_url}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackEvent("firm_rsvp_click", {
              firm_slug: event.firm_slug,
              event_id: event.id,
              event_type: event.event_type,
            })}
            data-testid={`event-rsvp-btn-${event.id}`}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#4C1D95] text-white text-sm font-medium px-3 py-1.5 hover:bg-[#3B0764] transition-colors"
          >
            RSVP <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <span />
        )}
        {isOwner && event.status !== "cancelled" && event.status !== "rejected" && (
          <div className="flex items-center gap-3">
            {onEdit && (
              <button
                data-testid={`event-edit-btn-${event.id}`}
                onClick={() => onEdit(event)}
                className="text-xs text-[#4C1D95] hover:text-[#3B0764] inline-flex items-center gap-1"
              >
                <Pencil className="h-3 w-3" /> Edit
              </button>
            )}
            {onCancel && (
              <button
                data-testid={`event-cancel-btn-${event.id}`}
                onClick={() => onCancel(event.id)}
                className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
              >
                <Trash2 className="h-3 w-3" /> Cancel event
              </button>
            )}
          </div>
        )}
      </footer>
    </article>
  );
}

function CompactEventCard({ event }) {
  const typeLabel = EVENT_TYPE_LABEL[event.event_type] || event.event_type;
  return (
    <a
      href={event.rsvp_url || "#"}
      target={event.rsvp_url ? "_blank" : undefined}
      rel="noreferrer"
      onClick={() => event.rsvp_url && trackEvent("firm_rsvp_click", {
        firm_slug: event.firm_slug,
        event_id: event.id,
        event_type: event.event_type,
        source: "compact",
      })}
      data-testid={`event-card-compact-${event.id}`}
      className="flex items-center gap-3 rounded-md border border-stone-200 bg-white px-3 py-2 hover:border-[#4C1D95]/40 transition-colors text-left"
    >
      <div className="shrink-0 rounded-md bg-[#F5F0FF] p-1.5">
        <CalendarDays className="h-4 w-4 text-[#4C1D95]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-stone-900 truncate">{event.title}</div>
        <div className="text-xs text-stone-500 mt-0.5">
          {event.firm_name} · {typeLabel} · {fmtDateTimeRange(event.starts_at, event.ends_at)}
        </div>
      </div>
      <ExternalLink className="h-3.5 w-3.5 text-stone-400 shrink-0" />
    </a>
  );
}
